const escapeHtml = (value) =>
  String(value ?? '')?.replace(/&/g, '&amp;')?.replace(/</g, '&lt;')?.replace(/>/g, '&gt;')?.replace(/"/g, '&quot;');

/**
 * Print a chart: clones the rendered SVG chart + a data table into a print window.
 * @param {Object} options
 * @param {string} options.title
 * @param {string} [options.subtitle]
 * @param {HTMLElement} [options.chartElement] - element containing the recharts SVG
 * @param {string[]} options.columns - table header labels
 * @param {Array<Array<string|number>>} options.rows - table rows
 */
export const printChart = ({ title, subtitle, chartElement, columns = [], rows = [] }) => {
  const svg = chartElement?.querySelector?.('svg');
  let svgMarkup = '';
  if (svg) {
    const clone = svg?.cloneNode(true);
    const rect = svg?.getBoundingClientRect();
    clone?.setAttribute('width', String(Math.round(rect?.width || 700)));
    clone?.setAttribute('height', String(Math.round(rect?.height || 280)));
    clone?.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgMarkup = new XMLSerializer()?.serializeToString(clone);
  }

  const rootStyles = getComputedStyle(document.documentElement);
  const cssVars = ['--color-primary', '--color-accent', '--color-border', '--color-muted', '--color-muted-foreground', '--color-foreground']
    ?.map((v) => `${v}:${rootStyles?.getPropertyValue(v) || '#888'};`)
    ?.join('');

  const head = columns?.map((c, i) => `<th class="${i === 0 ? 'l' : 'r'}">${escapeHtml(c)}</th>`)?.join('');
  const body = rows
    ?.map(
      (row) =>
        `<tr>${row
          ?.map((cell, i) => `<td class="${i === 0 ? 'l' : 'r'}">${escapeHtml(cell)}</td>`)
          ?.join('')}</tr>`
    )
    ?.join('');

  const printedOn = new Date()?.toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  :root{${cssVars}}
  *{box-sizing:border-box}
  body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px}
  h1{font-size:20px;margin:0 0 4px}
  p.sub{font-size:12px;color:#666;margin:0 0 16px}
  .chart{margin:12px 0 20px;width:100%;overflow:hidden}
  .chart svg{max-width:100%;height:auto}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th,td{border-bottom:1px solid #ddd;padding:6px 8px}
  th{background:#f5f5f5}
  .l{text-align:left}.r{text-align:right}
  .foot{margin-top:16px;font-size:11px;color:#888}
  @media print{body{margin:12mm}}
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="sub">${escapeHtml(subtitle || '')}</p>
  <div class="chart">${svgMarkup}</div>
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
  <p class="foot">Imprimé le ${escapeHtml(printedOn)}</p>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return false;
  win?.document?.open();
  win?.document?.write(html);
  win?.document?.close();
  win?.focus();
  setTimeout(() => {
    win?.print();
  }, 350);
  return true;
};

export default printChart;
